import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const ProjectModal = ({ project, onClose }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Function to get correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.jpg';

    if (imagePath.startsWith('https')) return imagePath;

    if (imagePath.startsWith('/uploads')) {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://alafoogarage.com/ahalafoo';
      return `${baseUrl}${imagePath}`;
    }

    return `${process.env.REACT_APP_API_URL || 'https://alafoogarage.com/ahalafoo'}${imagePath}`;
  };

  const photos = project.photos && project.photos.length > 0
    ? project.photos.map(photo => getImageUrl(photo))
    : [getImageUrl(project.mainPhoto) || '/placeholder.jpg'];

  const nextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) =>
      prevIndex === photos.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prevIndex) =>
      prevIndex === 0 ? photos.length - 1 : prevIndex - 1
    );
  };

  // Auto slideshow effect
  useEffect(() => {
    if (photos.length <= 1) return; // No slideshow if only one photo

    const interval = setInterval(() => {
      nextPhoto();
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [photos]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-BH', {
      style: 'currency',
      currency: 'BHD'
    }).format(amount || 0);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>

          <div className="modal-header">
            <span className="project-category">{project.category}</span>
            <h2 style={{ color: 'white' }}>{project.projectName}</h2>
          </div>

          <div className="modal-body">
            <div className="project-gallery">
              <div className="main-photo">
                <img
                  src={photos[currentPhotoIndex]}
                  alt={project.projectName}
                  onError={(e) => {
                    console.error('Image failed to load:', photos[currentPhotoIndex]);
                    e.target.src = '/placeholder.jpg';
                  }}
                />
                {photos.length > 1 && (
                  <>
                    <button className="nav-button prev" onClick={prevPhoto}>
                      <FaChevronLeft />
                    </button>
                    <button className="nav-button next" onClick={nextPhoto}>
                      <FaChevronRight />
                    </button>
                  </>
                )}
                <div className="photo-counter">
                  {currentPhotoIndex + 1} / {photos.length}
                </div>
              </div>

              {photos.length > 1 && (
                <div className="photo-thumbnails">
                  {photos.map((photo, index) => (
                    <img
                      key={index}
                      src={photo}
                      alt={`${project.projectName} ${index + 1}`}
                      className={index === currentPhotoIndex ? 'active' : ''}
                      onClick={() => setCurrentPhotoIndex(index)}
                      onError={(e) => {
                        console.error('Thumbnail failed to load:', photo);
                        e.target.src = '/placeholder.jpg';
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="project-details">
              <h3>Project Details</h3>
             <p style={{ whiteSpace: 'pre-line' }}>{project.content}</p>

              <div className="project-info-grid">
                {/* <div className="info-item">
                  <span className="label">Status:</span>
                  <span className={`value ${project.completed ? 'completed' : 'in-progress'}`}>
                    {project.status}
                  </span>
                </div> */}

                {project.progress && (
                  <div className="info-item">
                    <span className="label">Progress:</span>
                    <span className="value">{project.progress}% Complete</span>
                  </div>
                )}

                {project.startDate && (
                  <div className="info-item">
                    <span className="label">Start Date:</span>
                    <span className="value">{formatDate(project.startDate)}</span>
                  </div>
                )}

                {project.completed && project.completionDate ? (
                  <div className="info-item">
                    <span className="label">Completion Date:</span>
                    <span className="value">{formatDate(project.completionDate)}</span>
                  </div>
                ) : project.startDate && (
                  <div className="info-item">
                    <span className="label">Status:</span>
                    <span className="value">In Progress</span>
                  </div>
                )}

                {project.contractValue && (
                  <div className="info-item">
                    <span className="label">Contract Value:</span>
                    <span className="value">{formatCurrency(project.contractValue)}</span>
                  </div>
                )}

                {project.client && (
                  <div className="info-item">
                    <span className="label">Client:</span>
                    <span className="value">{project.client}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProjectModal;
